import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Wind, Check, ChevronDown, ChevronUp, MapPin, ArrowRight, Phone } from 'lucide-react';
import BeforeAfterSection from '../components/BeforeAfterSection';

const CanopyDuctFanCleaning = ({ onQuoteClick }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "How often should a commercial kitchen exhaust system be cleaned?",
      a: "It depends on how much cooking your kitchen does and what kind. High-volume kitchens, like fast food or high-turnover restaurants, generally need cleaning more often, sometimes every few months, while lighter-use kitchens can go longer between cleans. We can assess your site and recommend a schedule in line with AS 1851:2012."
    },
    {
      q: "Do you provide compliance certificates?",
      a: "Yes. Every job includes a certificate of completion along with before and after photos, so you have documentation ready if it's ever requested by an insurer, council or auditor."
    },
    {
      q: "Do you service areas outside Perth?",
      a: "Yes. Alongside the Perth metro area, we regularly service Kalgoorlie, Esperance, Albany, Busselton, Bunbury, Margaret River and Australind."
    },
    {
      q: "What's the difference between canopy cleaning and duct cleaning?",
      a: "The canopy or hood is the part directly above the cooking equipment, while the ductwork carries extracted air (and the grease it picks up) out through the roof. Grease builds up in both, so a proper clean covers the whole system, canopy, ductwork and rooftop fan, rather than just the visible hood."
    }
  ];

  // Service Schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Canopy, Duct & Rooftop Fan Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://www.extreme-clean.com.au/#organization",
      "name": "Extreme Clean Australia"
    },
    "areaServed": [
      "Perth", "Osborne Park", "Malaga", "Kewdale", "Welshpool", "Canning Vale", 
      "Bibra Lake", "Jandakot", "Wangara", "Balcatta", "Bayswater", "Rockingham", 
      "Mandurah", "Joondalup", "Midland", "Yanchep", "Leederville", "Northbridge", 
      "Mount Lawley", "Fremantle", "Cockburn", "Landsdale", "Hope Valley", 
      "Kalgoorlie", "Esperance", "Albany", "Busselton", "Bunbury", "Margaret River", "Australind", "Geraldton"
    ],
    "description": "Grease build-up in kitchen exhaust canopies, ductwork and rooftop fans is one of the leading causes of commercial kitchen fires. The business has been cleaning these systems since 1999, and has operated as Extreme Clean Australia since 2006."
  };

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/services/canopy-duct-fan-cleaning-perth" />
        <title>Canopy, Duct & Rooftop Fan Cleaning Perth | Extreme Clean Australia</title>
        <meta name="description" content="AS 1851:2012 compliant kitchen exhaust cleaning in Perth and WA. Canopy, duct and rooftop fan cleaning for commercial kitchens. 20+ years experience with McDonald's, KFC, Domino's." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      <div className="pt-24 lg:pt-44" data-testid="canopy-duct-fan-page">
        {/* Hero - Full width background style */}
        <section className="bg-[#0A0A0A] py-24 relative" data-testid="service-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1734516893388-db40cbdd4d20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxjb21tZXJjaWFsJTIwa2l0Y2hlbiUyMGV4aGF1c3QlMjBob29kJTIwc3RhaW5sZXNzJTIwc3RlZWwlMjB2ZW50aWxhdGlvbnxlbnwwfHx8fDE3ODg5NTE0MzJ8MA&ixlib=rb-4.1.0&q=85"
              alt="Commercial kitchen with exhaust hood system"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6] bg-opacity-20 px-4 py-2 mb-6">
                <Wind size={20} className="text-[#3B82F6]" />
                <span className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Exhaust Cleaning</span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl !leading-[1.2] mb-6 text-white">
                Canopy, Duct & Rooftop Fan Cleaning Perth
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                AS 1851:2012 compliant cleaning for commercial kitchen exhaust systems across Perth and WA
              </p>
              <p className="text-gray-400 mb-8">
                Grease build-up in kitchen exhaust canopies, ductwork and rooftop fans is one of the leading causes of commercial kitchen fires. The business has been cleaning these systems since 1999, and has operated as Extreme Clean Australia since 2006. Starting with Domino's ovens and growing into cleaning for 60+ KFC stores and 30+ McDonald's sites across Western Australia. Every job is cleaned and documented to AS 1851:2012, with before and after photos and a certificate of completion, so you've got the paperwork ready for your insurer, council or next audit.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onQuoteClick}
                  className="bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
                <a
                  href="tel:0861875523"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> Call 08 6187 5523
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Complete System</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">What's Included</h2>
              <p className="text-gray-600 mb-8">
                A full canopy, duct and rooftop fan clean covers the entire exhaust system, not just the visible parts. We strip down and clean kitchen exhaust canopies and hoods, grease filters, the full run of ductwork, and rooftop extraction fans and motors, removing grease, oil and carbon build-up throughout. Everything is put back together, tested, and documented before we leave site.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Kitchen exhaust canopies & hoods',
                  'Grease filters',
                  'Full ductwork run',
                  'Rooftop extraction fans & motors',
                  'Grease, oil & carbon removal',
                  'Testing & documentation'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={20} className="text-[#3B82F6] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Businesses Choose Us */}
        <section className="py-20 bg-[#273268]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Track Record</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6 text-white">Why Businesses Choose Extreme Clean Australia</h2>
              <p className="text-gray-300">
                Twenty years of doing this work for major food-service brands means we know what an inspector or franchise auditor is actually looking for. We've held the full KFC exhaust contract for Western Australia (60+ stores) and grown McDonald's canopy and rooftop fan cleaning from 3 sites to 30+, alongside ongoing work for Domino's, The Coffee Club and Baptist Care. Extreme Clean Australia also cleans canopies, rooftop fans, small ovens and fryers at Adventure World. That's not marketing language, it's the track record. We're particularly active in Perth's café and restaurant areas, including Leederville, Northbridge, Mount Lawley and Fremantle.
              </p>
            </div>
          </div>
        </section>

        {/* Compliance & Documentation */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Compliance</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Compliance & Documentation</h2>
              <p className="text-gray-600 mb-8">
                Every canopy, duct and fan clean is carried out to AS 1851:2012 standards. You'll receive before and after photos of the work and a certificate of completion for every job, giving you a clear compliance record without having to chase it down later.
              </p>
              <div className="bg-white p-6 border-l-4 border-[#3B82F6]">
                <p className="font-heading font-bold text-lg mb-2">Every job includes:</p>
                <ul className="space-y-2 text-gray-600">
                  <li>• Before and after photographs</li>
                  <li>• Certificate of completion</li>
                  <li>• AS 1851:2012 compliance documentation</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Before and After */}
        <BeforeAfterSection
          title="Before and After"
          sectionId="canopy-before-after"
          pairs={[
            {
              images: [
                {
                  src: '/images/before-after/canopy-before.jpg',
                  alt: 'Commercial kitchen exhaust canopy with heavy grease build-up before cleaning',
                  label: 'Before'
                },
                {
                  src: '/images/before-after/canopy-after.jpg',
                  alt: 'Commercial kitchen exhaust canopy with restored stainless steel after cleaning',
                  label: 'After'
                }
              ]
            }
          ]}
        />

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Common Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                      aria-expanded={openFaq === index}
                    >
                      <span className="font-heading font-bold text-lg pr-4">{faq.q}</span>
                      {openFaq === index ? (
                        <ChevronUp size={20} className="text-[#3B82F6] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Service */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin size={20} className="text-[#3B82F6]" />
                <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Service Areas</p>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Areas We Service</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Perth Metro</h3>
                  <p className="text-gray-600">
                    We service the entire Perth metro area, from Yanchep in the north to Mandurah in the south, including Osborne Park, Malaga, Kewdale, Welshpool, Canning Vale, Bibra Lake, Jandakot, Wangara, Balcatta, Bayswater, Rockingham, Joondalup and Midland.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Regional WA</h3>
                  <p className="text-gray-600">
                    Kalgoorlie, Esperance, Albany, Busselton, Bunbury, Margaret River, Australind, Geraldton.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Related</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Related Services</h2>
              <p className="text-gray-600 mb-6">
                Canopy, duct and rooftop fan cleaning often pairs with commercial kitchen cleaning as part of a complete kitchen maintenance routine.
              </p>
              <Link 
                to="/services/commercial-kitchen-cleaning-perth" 
                className="inline-flex items-center gap-2 bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-6 py-3 text-sm transition-all"
              >
                Commercial Kitchen Cleaning <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3B82F6]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to Get Your Exhaust System Cleaned?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free quote for AS 1851:2012 compliant canopy, duct and rooftop fan cleaning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
              >
                Get Free Quote
              </button>
              <a
                href="tel:0861875523"
                className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
              >
                <Phone size={18} /> Call 08 6187 5523
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CanopyDuctFanCleaning;
